import ScrollReveal from "@/components/ScrollReveal";
import { ArrowDown, FileText } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const credentials = [
  "Q-Day Hardened",
  "SOC 2 Type II",
  "ISO 27001",
  "NIST PQC Compliant",
  "Basel III/IV Ready",
];

export default function HeroSection() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center items-center text-center pt-24 pb-0 px-6 overflow-hidden"
    >
      {/* Radial gradient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% -10%, rgba(15, 26, 51, 0.9) 0%, transparent 60%), radial-gradient(ellipse 40% 30% at 50% 30%, rgba(212, 175, 55, 0.03) 0%, transparent 70%)",
        }}
      />

      {/* Subtle grid lines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: "linear-gradient(rgba(148,163,184,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.3) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Decorative gold rule above Est. */}
        <ScrollReveal>
          <div className="flex items-center justify-center gap-4 mb-6">
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: 48 }}
              transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="h-px bg-gradient-to-r from-transparent to-[#D4AF37]"
            />
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]/70 font-sans">
              Est. 2024
            </p>
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: 48 }}
              transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="h-px bg-gradient-to-l from-transparent to-[#D4AF37]"
            />
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <h1
            data-testid="hero-heading"
            className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.08] mb-6"
          >
            Preserving Value
            <br />
            <span className="text-[#D4AF37]">in the Post-Quantum Era.</span>
          </h1>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <p
            data-testid="hero-subtext"
            className="text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto mb-10"
          >
            J. Carter Asset Management delivers sovereign-grade infrastructure,
            custody, and intelligence for institutions navigating the new era of
            digital value preservation.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.3}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-10">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("contact")}
              className="group relative border border-[#D4AF37] text-[#D4AF37] px-10 py-4 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300 overflow-hidden"
            >
              <span className="relative z-10">Schedule Consultation</span>
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("overview")}
              className="text-slate-400 hover:text-white text-sm tracking-wide transition-colors duration-300 flex items-center gap-2"
            >
              Explore Our Services
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.35}>
          <a
            data-testid="hero-download-pdf"
            href={`${BACKEND_URL}/api/documents/institutional-overview`}
            className="inline-flex items-center gap-2.5 text-xs text-slate-400 hover:text-white tracking-[0.1em] transition-colors duration-300 border border-slate-800 hover:border-slate-600 px-6 py-3 mb-16"
          >
            <FileText className="w-3.5 h-3.5" />
            Download Institutional Overview (PDF)
          </a>
        </ScrollReveal>

        {/* Compliance Credentials Bar */}
        <ScrollReveal delay={0.4}>
          <div data-testid="hero-credentials" className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {credentials.map((cred, i) => (
              <div key={cred} className="flex items-center gap-3">
                {i > 0 && <span className="hidden sm:block w-px h-3 bg-slate-700" />}
                <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-sans">
                  {cred}
                </span>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>

      {/* Bottom: Jurisdictions banner */}
      <div className="relative z-10 mt-auto w-full border-t border-slate-800/50 py-5">
        <p className="text-center text-[10px] tracking-[0.25em] uppercase text-slate-600">
          Trusted by institutions across 14 jurisdictions worldwide
        </p>
      </div>
    </section>
  );
}
